/*
  MA · Reveal on scroll.

  Zero per-section markup: every .ma-section gets its content revealed as it
  enters the viewport. Pieces (headings, copy, buttons, cards, tiles, media)
  fade + rise with a short stagger. Works for any section on any page.

  Control:
    <section data-ma-reveal="off">          skip a section
    <div data-ma-reveal>                    force this element to be a piece
    <div data-ma-reveal-group>              stagger this element's children
  Global on/off: Theme settings → Animations → "Reveal sections on scroll".
*/
(() => {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const PIECE = [
    '[data-ma-reveal]', '.ma-section-header', '.ma-btn',
    'h1', 'h2', 'h3', 'p', 'blockquote', 'hr', 'form',
    '[class*="__card"]', '[class*="__tile"]', '[class*="__item"]', '[class*="__post"]',
    '[class*="__logo"]', '[class*="__stat"]', '[class*="__media"]', '[class*="__frame"]',
    '[class*="__cell"]', '[class*="__hex"]', '[class*="__slide"]', '[class*="__quote"]',
    '[class*="__badge"]', '[class*="__pill"]', '[class*="__image"]', '[class*="__figure"]',
  ].join(',');

  const prep = (section) => {
    if (section.dataset.maReveal === 'off' || section.dataset.maRevealReady) return;
    section.dataset.maRevealReady = '1';
    let pieces = [...section.querySelectorAll(PIECE)];
    // Outermost match wins — a card reveals as one unit, not its h3 and p separately.
    pieces = pieces.filter((el) => !pieces.some((o) => o !== el && o.contains(el)));
    // Fixed UI inside a section (PDP sticky add-to-cart bar) must not fade with the section.
    pieces = pieces.filter((el) => !el.closest('[data-ma-reveal="off"]'));
    section.querySelectorAll('[data-ma-reveal-group]').forEach((g) => {
      pieces = pieces.filter((el) => !g.contains(el));
      pieces.push(...g.children);
    });
    pieces.forEach((el, i) => {
      el.classList.add('ma-reveal');
      el.style.setProperty('--ma-reveal-i', Math.min(i, 12));
    });
    return pieces;
  };

  // Both directions: a section leaving the viewport hides again, so scrolling
  // back up replays it and the page reads as one connected surface.
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      e.target.classList.toggle('is-in', e.isIntersecting);
      e.target.querySelectorAll('.ma-reveal').forEach((el) => el.classList.toggle('is-in', e.isIntersecting));
    });
  }, { rootMargin: '-8% 0px -8% 0px', threshold: 0 });

  const init = (root = document) => {
    root.querySelectorAll('.ma-section').forEach((s) => { prep(s); io.observe(s); });
  };

  init();

  // Announcement strip "slide" mode: cycle .is-current through the items.
  document.querySelectorAll('.ma-header__announcement--rotate').forEach((bar) => {
    const items = [...bar.querySelectorAll('.ma-header__announcement-track:not([aria-hidden]) .ma-header__announcement-item')];
    if (items.length < 2) { items[0] && items[0].classList.add('is-current'); return; }
    const speed = parseFloat(getComputedStyle(bar).getPropertyValue('--ma-marquee-speed')) || 24;
    let i = 0; items[0].classList.add('is-current');
    setInterval(() => {
      const prev = items[i]; i = (i + 1) % items.length;
      prev.classList.remove('is-current'); prev.classList.add('is-leaving');
      items[i].classList.add('is-current');
      setTimeout(() => prev.classList.remove('is-leaving'), 600);
    }, Math.max(2500, speed * 250));
  });

  // Theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  // Elements already in view on a slow observer start still get shown.
  addEventListener('load', () => document.querySelectorAll('.ma-section:not(.is-in)').forEach((s) => {
    const r = s.getBoundingClientRect();
    if (r.top < innerHeight && r.bottom > 0) [s, ...s.querySelectorAll('.ma-reveal')].forEach((el) => el.classList.add('is-in'));
  }));
})();
